import type { AdministratorMfaEnrollmentResult } from "@reminder-tracker/backend-contracts";
import { useAction, useQuery } from "convex/react";
import { useState, type FormEvent, type ReactNode } from "react";

import {
  getAdministratorMfaStatus,
  startAdministratorMfaChallenge,
  startAdministratorMfaEnrollment,
  verifyAdministratorMfaCode,
} from "../../backend/functions";

function newCorrelationId(): string {
  if (typeof globalThis.crypto.randomUUID !== "function") {
    throw new Error("A secure correlation generator is unavailable in this browser.");
  }
  return `corr_${globalThis.crypto.randomUUID().replaceAll("-", "")}`;
}

type PendingOperation = "enrolling" | "challenging" | "verifying" | null;

export function AdministratorMfaGate({
  children,
  scope = "operations",
}: Readonly<{ children: ReactNode; scope?: "operations" | "catalogue" }>): React.JSX.Element {
  const [statusCorrelationId] = useState(newCorrelationId);
  const [enrollment, setEnrollment] = useState<AdministratorMfaEnrollmentResult | null>(null);
  const [challengeReady, setChallengeReady] = useState(false);
  const [code, setCode] = useState("");
  const [pending, setPending] = useState<PendingOperation>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const status = useQuery(getAdministratorMfaStatus, { correlationId: statusCorrelationId, scope });
  const startEnrollment = useAction(startAdministratorMfaEnrollment);
  const startChallenge = useAction(startAdministratorMfaChallenge);
  const verifyCode = useAction(verifyAdministratorMfaCode);

  if (status === undefined) {
    return <p aria-live="polite">Checking administrator authenticator status…</p>;
  }
  if (status.state === "verified") return <>{children}</>;

  const enrollmentChallengeReady = status.state === "enrollment-pending" || enrollment !== null;
  const canEnterCode = enrollmentChallengeReady || challengeReady;

  async function enroll(): Promise<void> {
    setPending("enrolling");
    setFeedback(null);
    try {
      const result = await startEnrollment({ correlationId: newCorrelationId() });
      setEnrollment(result);
      setFeedback("Scan the QR code, then enter the current six-digit code.");
    } catch {
      setFeedback("Authenticator enrollment could not be started. Try again or contact support.");
    } finally {
      setPending(null);
    }
  }

  async function challenge(): Promise<void> {
    setPending("challenging");
    setFeedback(null);
    try {
      await startChallenge({ correlationId: newCorrelationId() });
      setChallengeReady(true);
      setFeedback("Enter the current six-digit code from your authenticator app.");
    } catch {
      setFeedback("A current authenticator challenge could not be started. Try again shortly.");
    } finally {
      setPending(null);
    }
  }

  async function verify(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!/^\d{6}$/u.test(code)) {
      setFeedback("Enter the complete six-digit code.");
      return;
    }
    setPending("verifying");
    setFeedback(null);
    try {
      await verifyCode({ correlationId: newCorrelationId(), code });
      setCode("");
      setFeedback("Administrator authenticator verified.");
    } catch {
      setFeedback("That code was not accepted. Enter the current six-digit code and try again.");
    } finally {
      setPending(null);
    }
  }

  return (
    <section className="mfa-gate" aria-labelledby="administrator-mfa-title">
      <p className="eyebrow">Required administrator control</p>
      <h1 id="administrator-mfa-title">Verify your authenticator</h1>
      <p className="mfa-intro">
        Site Administration requires a time-based authenticator code before aggregate operational
        information is available. This verification applies only to the current signed-in session.
      </p>

      {status.state === "enrollment-required" && enrollment === null ? (
        <button type="button" disabled={pending !== null} onClick={() => void enroll()}>
          {pending === "enrolling" ? "Preparing authenticator…" : "Set up authenticator"}
        </button>
      ) : null}

      {enrollment !== null ? (
        <figure className="mfa-enrollment">
          <img
            src={enrollment.qrCode}
            alt="QR code for the Reminder Tracker Site Administration authenticator"
            width="224"
            height="224"
          />
          <figcaption>
            Scan this once with your authenticator app. The underlying seed is never stored by
            Reminder Tracker.
          </figcaption>
        </figure>
      ) : null}

      {status.state === "verification-required" && !challengeReady ? (
        <button type="button" disabled={pending !== null} onClick={() => void challenge()}>
          {pending === "challenging" ? "Starting verification…" : "Verify with authenticator"}
        </button>
      ) : null}

      {canEnterCode ? (
        <form className="mfa-form" onSubmit={(event) => void verify(event)}>
          <label htmlFor="administrator-totp-code">Six-digit authenticator code</label>
          <div className="mfa-entry">
            <input
              id="administrator-totp-code"
              name="administrator-totp-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              disabled={pending !== null}
              onChange={(event) => setCode(event.target.value.replace(/\D/gu, "").slice(0, 6))}
              aria-describedby="administrator-mfa-feedback"
            />
            <button type="submit" disabled={pending !== null || code.length !== 6}>
              {pending === "verifying" ? "Verifying…" : "Continue"}
            </button>
          </div>
        </form>
      ) : null}

      {status.state === "enrollment-pending" && enrollment === null ? (
        <p className="mfa-recovery-note">
          Enrollment has already started for this administrator. Use the authenticator you scanned
          in this session. If setup was interrupted before scanning, contact support; Reminder
          Tracker will not reveal or reconstruct the seed.
        </p>
      ) : null}

      <p id="administrator-mfa-feedback" className="mfa-feedback" aria-live="polite">
        {feedback}
      </p>
    </section>
  );
}
